/**
 * 建议分值（suggest_score）相关的公共计算逻辑。
 *
 * 原先只在 ScoreTableSheet 里用于「建议分值」列的展示；
 * 现在加载评分表时也要用它给「权重打分」赋默认值，
 * 因此抽到这里由展示层与 store 共用，避免两处各写一份最大余数法。
 *
 * 取值规则：
 * - 第 1 轮：取评分表 binding 上的 suggest_score（管理员创建评分表时配置）
 * - 第 2 轮及以后：取上一轮均值 mean，保留 1 位小数，并在同层同父级内
 *   做最小尾差校正使合计 = 100（不再使用整数「最大余数法」）
 */

/**
 * 把一组因子均值规整为「保留 1 位小数、且合计 = 100」的建议分值。
 *
 * 与之前的「最大余数法（整数分配）」不同：
 * - 不再强制取整为整数，每个值保留 1 位小数（如 33.3、12.7）；
 * - 直接以「上一轮均值」作为建议分值。
 *
 * 关键：全程用「十分之一」整数运算，彻底规避浮点误差——每个均值先四舍五入成
 * 整数（单位 0.1），再在整数域内把合计校正到 1000（即 100.0），保证该组合计
 * 精确为 100，不会出现 100.1 / 100.00000000000001 之类的浮点尾差。
 *
 * @param {{id:any, mean:number|null}[]} items 因子列表（含 id 与 mean）
 * @returns {Object} { [id]: number } 仅包含 mean 非空的因子
 */
const normalizeMeansToHundredDecimal = (items) => {
  const valid = (items || []).filter((it) => it && it.mean != null);
  const out = {};
  if (!valid.length) return out;

  // 1) 每个均值四舍五入到整数（单位 0.1）
  const tenth = valid.map((it) => Math.round(it.mean * 10));
  // 2) 整数域内计算合计与 1000 的差，逐步 ±1 校正到精确为 1000
  let diffTenth = 1000 - tenth.reduce((a, b) => a + b, 0);

  // 校正优先级：原均值与四舍五入后差值（被舍掉的小数）最大的因子先补，扰动最小
  const loss = valid.map((it, i) => it.mean * 10 - tenth[i]); // 仅用于排序的近似
  const order = loss
    .map((l, i) => ({ i, l }))
    .sort((a, b) => b.l - a.l);

  let k = 0;
  const maxIter = order.length * 3 + 10;
  while (diffTenth !== 0 && k < maxIter) {
    const step = diffTenth > 0 ? 1 : -1;
    const idx = order[k % order.length].i;
    tenth[idx] += step;
    diffTenth -= step;
    k++;
  }

  valid.forEach((it, i) => {
    out[it.id] = tenth[i] / 10;
  });
  return out;
};

/**
 * 第 2 轮起预计算「建议分值校正表」：三层各自用上一轮均值（保留 1 位小数）
 * 归一化到合计 = 100。key 形如 `${level}-${factorId}`，value 为校正后的数值。
 * 第 1 轮无需校正（直接用 binding 的 suggest_score），返回空对象。
 */
export const buildSuggestMap = (tableInfo, rounds) => {
  const suggestMap = {};
  if (!tableInfo || !(rounds > 1)) return suggestMap;

  const basicList = tableInfo.get("basic") || [];
  const normalList = tableInfo.get("normal") || [];
  const subList = tableInfo.get("sub") || [];

  // 基本因素层：全部 basic 的均值 → 合计 100（保留 1 位小数）
  Object.assign(
    suggestMap,
    normalizeMeansToHundredDecimal(
      basicList.map((f) => ({ id: f.get("id"), mean: f.get("mean") }))
    )
  );

  // 派生因素层：每个 basic 块内的 normals 均值 → 合计 100
  basicList.forEach((b) => {
    const normals = normalList.filter(
      (n) => n.get("parent_id") === b.get("id")
    );
    Object.assign(
      suggestMap,
      normalizeMeansToHundredDecimal(
        normals.map((n) => ({ id: n.get("id"), mean: n.get("mean") }))
      )
    );
  });

  // 因子层：每个 normal 块内的 subs 均值 → 合计 100
  normalList.forEach((n) => {
    const subs = subList.filter((s) => s.get("parent_id") === n.get("id"));
    Object.assign(
      suggestMap,
      normalizeMeansToHundredDecimal(
        subs.map((s) => ({ id: s.get("id"), mean: s.get("mean") }))
      )
    );
  });

  return suggestMap;
};

/**
 * 单个因子的建议分值：
 * - rounds > 1：优先取 suggestMap（已按最大余数法校正到合计 100），
 *   未命中时兜底 Math.round(mean)
 * - rounds === 1：取 binding 的 suggest_score
 * 返回 null 表示「无建议值」（展示层显示 "—"，赋值时跳过该因子）
 */
export const getSuggestScore = (factor, rounds, suggestMap, level) => {
  if (rounds > 1) {
    const key = `${level}-${factor.get("id")}`;
    if (suggestMap && suggestMap[key] !== undefined) return suggestMap[key];
    const mean = factor.get("mean");
    return mean != null ? Math.round(mean * 10) / 10 : null;
  }
  return factor.get("suggest_score");
};

/**
 * 该表是否「一个数值都没填」（三层可填因子的 value 全为空）。
 * 用于判断能否用建议分值预填：后端已回填历史分数（如第 2 轮返回上一轮评分）
 * 时不能覆盖，只有真正空表才预填。
 */
export const isTableEmpty = (tableInfo) => {
  if (!tableInfo) return false;
  return !["basic", "normal", "sub"].some((level) => {
    const list = tableInfo.get(level);
    return (
      list &&
      list.some(
        (f) =>
          f.get("status", 1) !== 0 &&
          f.get("value") != null &&
          f.get("value") !== ""
      )
    );
  });
};

/**
 * 把建议分值赋给「权重打分」（value），返回新的 immutable 表对象。
 * - 只处理 status !== 0（可填）的因子
 * - 建议值为 null/undefined 的因子保持为空
 * - 直接使用建议分值原值，保证「权重打分」与同行「建议分值」完全一致
 */
export const applySuggestAsValue = (tableInfo, rounds) => {
  if (!tableInfo) return tableInfo;
  const suggestMap = buildSuggestMap(tableInfo, rounds);

  let result = tableInfo;
  ["basic", "normal", "sub"].forEach((level) => {
    const list = result.get(level);
    if (!list || !list.size) return;
    result = result.set(
      level,
      list.map((f) => {
        if (f.get("status", 1) === 0) return f;
        const suggest = getSuggestScore(f, rounds, suggestMap, level);
        return suggest == null ? f : f.set("value", suggest);
      })
    );
  });
  return result;
};
